/**
 * PB-FOUNDATION modal chrome — open/close, mobile keyboard, visual viewport.
 * PB-FND-32: extracted from pb-logic-core.liquid.
 */

import { runDraftContinuePresentationPass } from '@theme/pb-draft-continue';
import { isDeclarativeDerivedPropertiesSettings } from '@theme/pb-derived-properties-logic';
import { createFocusContainment } from '@theme/pb-focus-containment';
import {
  KEYBOARD_MIN_INSET,
  KEYBOARD_SHIM_ID,
  corroboratedDisplacement,
  ensureKeyboardShim,
  expectedKeyboardInset,
  hasTouchKeyboard,
  measureKeyboardInset,
  opensKeyboard,
} from '@theme/pb-keyboard-inset';

/**
 * @param {object} deps
 * @param {object} deps.pb — productBuilder instance
 */
export function createModalChrome(deps) {
  const pb = deps.pb || deps;
  let focusGeneration = 0;
  const pendingFocusTimers = new Set();

  function cancelPendingFocus() {
    focusGeneration += 1;
    pendingFocusTimers.forEach(clearTimeout);
    pendingFocusTimers.clear();
  }

  function deferModalFocus(callback, delay) {
    const timer = setTimeout(() => {
      pendingFocusTimers.delete(timer);
      if (document.visibilityState === 'hidden' || !document.body.classList.contains('builder-open')) return;
      callback();
    }, delay);
    pendingFocusTimers.add(timer);
  }

  function isBuilderModalMeasurable() {
    if (typeof window.__pbIsBuilderModalMeasurable === 'function') {
      return window.__pbIsBuilderModalMeasurable();
    }
    const modal = document.getElementById('builder-modal');
    if (!modal) return false;
    return modal.style.display !== 'none';
  }

  function shouldSkipDesignLibraryResumeForSession() {
    const manifest = window.PBEntryManifest?.readEntryManifest?.(window.builderState);
    return !!(
      manifest && window.PBEntryManifest?.shouldSkipDesignLibraryResume?.(manifest)
    );
  }

  function maybeResumeDesignLibrarySession() {
    if (shouldSkipDesignLibraryResumeForSession()) {
      if (typeof pb.checkStepValidity === 'function') pb.checkStepValidity();
      return;
    }
    if (window.PBDesignLibrary && typeof window.PBDesignLibrary.resumeBuilderSession === 'function') {
      window.PBDesignLibrary.resumeBuilderSession();
    } else if (typeof pb.checkStepValidity === 'function') {
      pb.checkStepValidity();
    }
  }

  function clearModalClosePending() {
    if (pb._modalCloseFallbackTimer) {
      clearTimeout(pb._modalCloseFallbackTimer);
      pb._modalCloseFallbackTimer = null;
    }
    if (pb._modalCloseContentEl && pb._modalCloseTransitionHandler) {
      pb._modalCloseContentEl.removeEventListener('transitionend', pb._modalCloseTransitionHandler);
      pb._modalCloseContentEl = null;
      pb._modalCloseTransitionHandler = null;
    }
  }

  function clearModalOpenPending() {
    if (pb._modalOpenFallbackTimer) {
      clearTimeout(pb._modalOpenFallbackTimer);
      pb._modalOpenFallbackTimer = null;
    }
    if (pb._modalOpenContentEl && pb._modalOpenTransitionHandler) {
      pb._modalOpenContentEl.removeEventListener('transitionend', pb._modalOpenTransitionHandler);
      pb._modalOpenContentEl = null;
      pb._modalOpenTransitionHandler = null;
    }
  }

  function getModalFocusables() {
    const modal = document.getElementById('builder-modal');
    if (!modal) return [];
    const sel =
      'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
    return Array.prototype.filter.call(modal.querySelectorAll(sel), (el) => el.offsetParent !== null);
  }

  const focusContainment = createFocusContainment({ rootId: 'product-builder-root' });

  function bindFocusTrap() {
    if (pb._focusTrapBound) return;
    pb._focusTrapHandler = function (e) {
      if (e.key !== 'Tab' || !document.body.classList.contains('builder-open')) return;
      const modal = document.getElementById('builder-modal');
      if (!modal || !modal.contains(document.activeElement)) return;
      const focusables = getModalFocusables();
      if (focusables.length === 0) return;
      const idx = focusables.indexOf(document.activeElement);
      if (e.shiftKey) {
        if (idx <= 0) {
          e.preventDefault();
          focusables[focusables.length - 1].focus();
        }
      } else if (idx === -1 || idx >= focusables.length - 1) {
        e.preventDefault();
        focusables[0].focus();
      }
    };
    document.addEventListener('keydown', pb._focusTrapHandler);
    pb._focusTrapBound = true;
  }

  function unbindFocusTrap() {
    if (!pb._focusTrapBound) return;
    document.removeEventListener('keydown', pb._focusTrapHandler);
    pb._focusTrapBound = false;
  }

  function getKeyboardShimInput() {
    // MKB-2: the element's shape lives in pb-keyboard-inset, which bootstrap also uses.
    return ensureKeyboardShim(document.getElementById('product-builder-root'));
  }

  function ensureInputVisible(input) {
    if (!input) return;
    const container = document.querySelector('#product-builder-root .pb-modal-body');
    if (!container || !container.contains(input)) return;

    const vv = window.visualViewport;
    const visibleTop = vv ? vv.offsetTop : 0;
    const visibleBottom = vv ? vv.height + vv.offsetTop : window.innerHeight;
    const padding = 12;

    const rect = input.getBoundingClientRect();
    let delta = 0;
    if (rect.bottom + padding > visibleBottom) {
      delta = rect.bottom - visibleBottom + padding;
    } else if (rect.top - padding < visibleTop) {
      delta = rect.top - visibleTop - padding;
    }
    if (delta !== 0) {
      const instantScroll = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
      container.scrollBy({ top: delta, behavior: instantScroll ? 'auto' : 'smooth' });
    }
  }

  function focusStepInput(containerID, prefix, step) {
    if (document.visibilityState === 'hidden') return;
    const root = document.querySelector(containerID);
    if (!root || root.hidden || root.inert) return;
    const customInputs = root.querySelector(`#${prefix}content-page-${step} .pb-custom-inputs`);
    if (customInputs) {
      let firstField = customInputs.querySelector('.pb-tk-field');
      if (!firstField) {
        const imgStep = root.querySelector(`#${prefix}img-page-${step}`);
        const preview = imgStep ? imgStep.querySelector('.pb-custom-preview') : null;
        firstField = preview ? preview.querySelector('.pb-tk-field') : null;
      }
      if (firstField) {
        const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const mobileStep = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
        firstField.scrollIntoView({ behavior: reducedMotion || mobileStep ? 'auto' : 'smooth', block: 'nearest' });
        const focusable = firstField.querySelector('.builder-input, .builder-btn');
        if (focusable) {
          try {
            focusable.focus();
          } catch (e) {}
        }
      }
      return;
    }
    const candidates = root.querySelectorAll(`#${prefix}content-page-${step} .builder-input`);
    let input = null;
    for (let i = 0; i < candidates.length; i++) {
      const inp = candidates[i];
      if (inp.classList.contains('pb-other-input')) {
        const wrap = inp.closest('.pb-other-input-wrap');
        if (wrap && getComputedStyle(wrap).display === 'none') continue;
      }
      input = inp;
      break;
    }
    if (!input) return;

    const isMobile = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    const isTouch = 'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0;
    const shouldForce = isMobile || isTouch;
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIOS) {
      const dummy = getKeyboardShimInput();
      if (dummy) {
        try {
          dummy.focus();
        } catch (e) {}
      }
    }

    try {
      input.focus({ preventScroll: true });
    } catch (e) {
      input.focus();
    }
    if (shouldForce && document.activeElement !== input) {
      input.click();
      input.focus();
    }
    if (shouldForce) ensureInputVisible(input);

    if (document.activeElement !== input) {
      deferModalFocus(() => {
        try {
          input.focus({ preventScroll: true });
        } catch (e) {
          input.focus();
        }
        if (shouldForce) ensureInputVisible(input);
      }, 40);
    }
  }

  function bindKeyboardWatch() {
    if (pb._keyboardWatchBound) return;
    // In the mobile keyboard layout the focused field is anchored above the keyboard and is
    // always visible, so there is nothing to scroll into view — and this watcher's instant
    // scrolling is pure movement. Not binding it beats guarding every call site.
    const layoutRoot = document.getElementById('product-builder-root');
    if (layoutRoot && layoutRoot.classList.contains('pb-mobile-keyboard-layout')) return;
    const handler = () => {
      if (!document.body.classList.contains('builder-open')) return;
      const active = document.activeElement;
      if (active && active.classList && active.classList.contains('builder-input')) {
        ensureInputVisible(active);
      }
    };
    pb._keyboardWatchHandler = handler;
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handler);
      window.visualViewport.addEventListener('scroll', handler);
    }
    window.addEventListener('resize', handler);
    pb._keyboardWatchBound = true;
  }

  function unbindKeyboardWatch() {
    if (!pb._keyboardWatchBound) return;
    const handler = pb._keyboardWatchHandler;
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', handler);
      window.visualViewport.removeEventListener('scroll', handler);
    }
    window.removeEventListener('resize', handler);
    pb._keyboardWatchBound = false;
  }

  /* --- MTFK-7: mobile keyboard inset, rebuilt on a simpler footing ----------
   *
   * Six rounds of this chased the keyboard with a CSS transition: measure the viewport,
   * ease the layout toward it. That race cannot be won. The browser only tells you the
   * keyboard's height once it is already moving or done, so any transition you start is
   * late by construction, and its duration is a guess at a number the phone owns. Coarse
   * readings restarted it, so it stretched and decelerated — "slides slowly into position".
   *
   * So it no longer animates. The inset is written the instant the viewport reports, with
   * no transition, no settle timer, no cache, no monotonic smoothing and no in-flight flag.
   * The layout is then never ahead of the viewport and never behind it: whatever the phone
   * says is true, is on screen. That is the closest a web page can get to riding the
   * keyboard's own animation, and it is a fraction of the moving parts.
   *
   * What stays, because it is proven: the shell pinned to the layout viewport and painting
   * white (nothing of the page can show), the inset measured from the shell's own rect
   * rather than window.innerHeight (correct whether or not the browser clamps fixed
   * elements to the visual viewport), and the document held still.
   */

  function isMobileViewport() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
  }

  /* --- MTFK-F: the document is locked, not corrected --------------------------------
   *
   * iOS Safari scrolls the DOCUMENT to reveal a focused input, and `overflow: hidden` on
   * html and body does not stop it — a device readout measured scrollY = 311 with both in
   * place. That scroll displaces the fixed shell, and any JS that corrects the shell moves
   * the focused input, which makes iOS reveal again: a feedback loop that never converges
   * (three different displacements were measured on the same phone in the same scenario).
   *
   * So the displacement is made impossible instead of corrected. `body { position: fixed }`
   * leaves the document with nothing to scroll — scrollHeight equals clientHeight — which is
   * the one lock iOS respects. The theme's own dialog.js locks its dialogs the same way.
   * The saved scroll position is put back when the builder closes.
   */
  function lockDocumentScroll() {
    if (pb._experienceSurface) return;
    // The flag alone is not trusted: a crash-recovery path (recoverBuilderScrollLock, or
    // the cart-edit presenter's cleanup) can clear the body styles without this module
    // hearing about it. If the styles are gone, the lock is gone — re-lock fresh rather
    // than run the builder over an unlocked document.
    if (pb._pbScrollLocked && document.body.style.position === 'fixed') return;
    pb._pbScrollLocked = true;
    pb._pbScrollLockY = window.scrollY || 0;
    // MTFK-I: the lock itself is a CLASS RULE in pb-css (body.builder-open/closing,
    // !important), because a third-party pop-up wipes body inline styles for its own
    // show/hide cycle and an inline-only lock dies with them (device-measured: scrollY 346
    // with the builder open). The saved offset rides on the ROOT element, which no pop-up
    // touches. The inline copies below stay as belt and braces for the instant between
    // this call and the class landing.
    document.documentElement.style.setProperty('--pb-scroll-lock-top', -pb._pbScrollLockY + 'px');
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = -pb._pbScrollLockY + 'px';
  }

  function unlockDocumentScroll() {
    if (pb._experienceSurface) return;
    if (!pb._pbScrollLocked) return;
    pb._pbScrollLocked = false;
    document.documentElement.style.removeProperty('--pb-scroll-lock-top');
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    // One restore on close — this is not the per-event scroll corrector MTFK-8 removed.
    // The class rule held the page regardless of the inline styles' fate, so the saved Y
    // is always the truth to put back.
    window.scrollTo({ top: pb._pbScrollLockY || 0, behavior: 'instant' });
    pb._pbScrollLockY = 0;
  }

  /**
   * Where the shell ends, and where sight ends. Read from the shell's own rect rather than
   * `window.innerHeight`, so it is correct whether or not the browser clamps
   * `position: fixed` to the visual viewport while the keyboard is up — iOS Safari does,
   * Chromium does not. The shell is never moved, so this can never chase itself.
   */
  function readViewportSample() {
    const vv = window.visualViewport;
    if (!vv) return null;
    const modal = document.getElementById('builder-modal');
    const rect = modal ? modal.getBoundingClientRect() : null;
    const has = rect && rect.height > 0;
    return {
      shellBottom: has ? rect.bottom : window.innerHeight,
      visibleBottom: vv.offsetTop + vv.height,
      viewportHeight: window.innerHeight,
      // MKB-4c: how far the layer is displaced, from the rect already in hand. Free — no
      // second read, so the one-rect budget above is untouched. Named `crop` deliberately:
      // the two names this region may not contain are the old top-inset loop's variables.
      crop: has ? Math.max(0, Math.round(-rect.top)) : 0,
    };
  }

  /**
   * The sheet is pulled up from the bottom of the shell by the keyboard's height. The shell
   * itself stays full-screen and white behind it, so nothing of the product page can show in
   * the gap. There is no top inset any more: the document scroll lock above means the shell
   * cannot be displaced, so the sheet's top is the shell's top, always.
   */
  /**
   * MKB-3 — the floor under every inset, now and in future.
   *
   * No reading, no guess and no arithmetic yet unwritten may leave the sheet shorter than
   * the controls it has to hold. That state is the original report 4: preview gone, question
   * gone, progress gone, only Continue on screen above a band of white. It is unrecoverable
   * without closing the builder, and it is one bad number away at all times — so the guard
   * lives at the single write, where nothing can route around it.
   *
   * Measured, not assumed: the control stack's own height is what the sheet must keep.
   */
  function maxUsableInset() {
    // Deliberately still declared under this exact name, and deliberately a delegate.
    // `tests/pb-mtfk-mobile-keyboard.test.mjs` slices the source with
    // `indexOf('function maxUsableInset()')` and counts the rect reads from there. Rename it
    // and that indexOf returns -1, the slice starts at the top of the file, and the tripwire
    // degenerates into something that passes by measuring nothing.
    return readSheetMetrics().maxUsable;
  }

  /**
   * MKB-4 — the sheet's four numbers, from the two rects the floor already reads.
   *
   * `crop` is the new one and it is free: the shell's rect was being read for its height,
   * and `rect.top` came with it. It answers "how far does the sheet's top sit above what is
   * visible" — device reading 7: `shl -346 -> 428`, so 346px of close button, product pill
   * and question are hanging off the top of the screen. `#builder-modal` is
   * `position: fixed; top: 0; height: 100%` and no ancestor creates a fixed-positioning
   * containing block, so its rect reports the fixed layer's own displacement and nothing
   * else. On iOS a displaced fixed layer's rect is already relative to the VISUAL viewport,
   * which is why -346 means "346px above what you can see" rather than "above the layout
   * origin". That is the assumption a from-first-principles fix would have got wrong.
   *
   * @returns {{ shellHeight: number, controlsHeight: number, maxUsable: number, crop: number }}
   */
  function readSheetMetrics() {
    const none = { shellHeight: 0, controlsHeight: 0, maxUsable: Infinity, crop: 0 };
    const shell = document.getElementById('builder-modal');
    const rect = shell ? shell.getBoundingClientRect() : null;
    if (!rect || !(rect.height > 0)) return none;
    const controls = document.querySelector('#product-builder-root .pb-bottom-wrapper');
    const controlsHeight = controls ? controls.getBoundingClientRect().height : 0;
    // A sheet with no controls to measure still keeps a quarter of the shell, which is the
    // cap `measureKeyboardInset` already applies — belt and braces, never less.
    const keep = controlsHeight > 0 ? controlsHeight : Math.round(rect.height * 0.25);
    return {
      shellHeight: rect.height,
      controlsHeight,
      maxUsable: Math.max(0, Math.round(rect.height - keep)),
      crop: Math.max(0, Math.round(-rect.top)),
    };
  }

  /**
   * MKB-4c — the room iOS has already made, floored for the life of this focus.
   *
   * ONE quantity with one owner, because there are two consumers and they must never
   * disagree: the inset arithmetic in `syncKeyboardInset`, and MKB-4's crop guard below.
   * The first cut floored only the inset and let the crop re-decide per frame, and a critic
   * found what that costs — on an unwitnessed frame the crop collapsed 347 to 0 and the close
   * button, pill and question snapped from 10 to -337 and back, which is the very shimmer the
   * floor exists to prevent, moved into the other vehicle. Same function is not the same rule.
   *
   * The floor itself: a corroborated displacement stands until the witnesses contradict it or
   * the focus ends. They are not obliged to move in lockstep — MTFK State D recorded
   * `vv.offsetTop 271` beside `scrollY 311` on one frame — and the scroll listener puts a
   * measurement on frames that used to have none, so a frame agreeing with neither is now
   * reachable mid-rise. Absence of evidence is not evidence the layer sprang back.
   *
   * A memory of a measurement, like `_pbKbKnownInset`; never a correction of anything. It
   * self-corrects the moment the witnesses agree again, including agreeing on zero, and it
   * expires with the focus and the screen width in `clearKeyboardExpectation`.
   */
  function displacementRoom(crop, pan) {
    if (!pb._pbKbFocused) return 0;
    const corroborated = corroboratedDisplacement(crop, pan, window.pageYOffset);
    if (corroborated !== null) pb._pbKbDisplaced = corroborated;
    return pb._pbKbDisplaced || 0;
  }

  /** The question keeps at least this much, whatever the crop would like to take. */
  const MIN_QUESTION_PX = 88;

  /**
   * MKB-4 — re-base the top chrome to the visible band. NEVER the sheet.
   *
   * Chosen 2026-08-29 by a three-way judged comparison with loop risk weighted heaviest. The
   * two rejected designs are written up in `docs/projects/mkb-master.md` so nobody re-opens
   * them; the short version is that driving the sheet's own `top` from a pan-derived number
   * is the exact shape of the top-inset loop that cost rounds 1-19. (Named without its old
   * property name on purpose: MTFK-F asserts that string appears nowhere in this file, and a
   * tripwire weakened by a comment is a tripwire weakened.)
   *
   * This writes one custom property that three CSS rules read: the close button's `top`, the
   * pill's `top`, and the preview pane's `padding-top`. Nothing it writes can move the sheet,
   * the control stack or the input, and `THE NO-FEEDBACK RECEIPT` in tests/pb-mkb-lab.test.mjs
   * asserts exactly that rather than claiming it in a paragraph.
   *
   * Three guards, in the order they bite:
   *
   * - `pb._pbKbFocused`, and never the inset or `body.pb-keyboard-open`. In the photographed
   *   state the inset is 0 and that class is off, so either would write 0 in the one state
   *   that matters.
   * - `agrees` — the identity that says rects are visual-viewport-relative. MTFK's State B
   *   is a recorded reading where the layer moved 347 and the pan stayed 0; there we do not
   *   know what the numbers mean, so we do what ships today, which is nothing.
   * - `cap` — the crop is padding inside the question's pane, so an unbounded one would
   *   squeeze the question to nothing on a tall control stack. It keeps MIN_QUESTION_PX.
   */
  function writeChromeCrop(m, bottom) {
    const root = document.getElementById('product-builder-root');
    if (!root) return;
    const vv = window.visualViewport;
    const pan = vv ? Math.max(0, Math.round(vv.offsetTop)) : 0;
    // MKB-4c: the SAME floored quantity the inset uses, not a second per-frame reading of
    // the same function. `displacementRoom` is the one owner; see its note above for what
    // happened when the crop was allowed to re-decide on its own.
    const agrees = displacementRoom(m.crop, pan) > 0;
    const room = m.shellHeight - bottom - m.controlsHeight;
    const cap = Math.max(0, room - MIN_QUESTION_PX);
    // MKB-4c — what `agrees` is worth now, said plainly rather than assumed.
    //
    // It was MKB-4's protection against re-basing the chrome by a displacement that is not
    // the keyboard's. A critic checked that claim against every reading the programme has and
    // it does not survive: iOS displaces a fixed layer BY scrolling the document under it, so
    // `m.crop` and `pageYOffset` are one quantity read two ways and agree in all nine
    // readings — the declining branch does not fire on a phone. `shovedUnwitnessed` in the
    // lab proves the branch exists and behaves; it is not evidence that it occurs.
    //
    // A keyboard-height cap was drafted to replace the lost protection and then deleted: the
    // reveal-shove IS the keyboard on every reading, so that bound cannot bite either, and a
    // second unfalsifiable guard is worse than none — it is dead code a later reader trusts.
    // `MIN_QUESTION_PX` below is the one bound here that demonstrably fires (THE CLAMP).
    //
    // If a reading ever shows a displacement that is not the keyboard's, that is the evidence
    // a real bound gets designed from. Until then this is honest about being a timing gate.
    const px = pb._pbKbFocused && agrees ? Math.min(m.crop, cap) : 0;
    // While a keyboard field holds focus the property is always WRITTEN, zero included. That
    // is instrumentation, not layout — `var(--pb-kb-crop, 0px)` reads the same either way —
    // and it is the whole basis of the round-21 decision in the master doc: `crp 0` has to
    // mean "it ran on a displaced layer and answered zero", which is the reading that kills
    // the coordinate hypothesis. Make zero unwritable and that state is unreportable.
    //
    // With no field focused there is nothing to correct, so the property comes off entirely
    // and `crp off` means exactly that, checkable against `kbF n` on the next line of the
    // readout. A build too old to have any of this shows no `crp` on the line at all.
    const want = pb._pbKbFocused ? px + 'px' : '';
    if (pb._pbChromeCrop === want) return;
    pb._pbChromeCrop = want;
    if (want) root.style.setProperty('--pb-kb-crop', want);
    else root.style.removeProperty('--pb-kb-crop');
  }

  /**
   * Drop the crop, property and all.
   *
   * Called on close, and on the way out of the mobile viewport — a rotation to landscape
   * stops `syncKeyboardInset` recomputing anything, and a correction left standing after the
   * thing it corrected is gone is this programme's signature failure. The CSS is gated at the
   * same breakpoint so this is belt and braces, which is the right number of belts here.
   */
  function clearChromeCrop() {
    if (pb._pbChromeCrop === '') return;
    pb._pbChromeCrop = '';
    const root = document.getElementById('product-builder-root');
    if (root) root.style.removeProperty('--pb-kb-crop');
  }

  function writeKeyboardInset(inset) {
    const root = document.getElementById('product-builder-root');
    if (!root) return;
    const wanted = Math.max(0, Math.round(inset));
    // The control stack's height depends on `body.pb-keyboard-open` — it drops its
    // safe-area padding when the keyboard covers the home indicator (see pb-css). Measuring
    // before that class is set reads the stack in the state we are leaving, not the one we
    // are entering, and clamps ~34px conservatively on the first write of every open. So the
    // class goes on first, and the measurement describes the layout it is about to produce.
    document.body.classList.toggle('pb-keyboard-open', wanted > 0);
    const m = readSheetMetrics();
    const bottom = Math.min(wanted, m.maxUsable);
    // ABOVE the dedupe below, and that placement is load-bearing. In the photographed state
    // the inset is 0 and the crop is 346, so a dedupe on the inset swallows the crop write
    // entirely — the one state this slice exists for would be the one it never reached.
    writeChromeCrop(m, bottom);
    if (pb._pbKbInset === bottom) return;
    pb._pbKbInset = bottom;
    root.style.setProperty('--pb-kb-inset', bottom + 'px');
    document.body.classList.toggle('pb-keyboard-open', bottom > 0);
  }

  /** The whole of it: measure, write. Nothing is smoothed, nothing is eased. */
  function syncKeyboardInset() {
    const body = document.body;
    // `builder-closing` is the sheet's slide, in both directions. Allowing it here is what
    // lets the sheet slide up ALREADY sized for a keyboard that is already open — one
    // movement instead of a slide followed by a resize.
    if (!body.classList.contains('builder-open') && !body.classList.contains('builder-closing')) {
      return;
    }
    if (!isMobileViewport()) {
      // Rotated out of the phone layout. Nothing below runs again until it comes back, so
      // anything already written would simply stand.
      clearChromeCrop();
      return;
    }
    if (!pb._pbKbFocused) {
      writeKeyboardInset(0);
      // MKB-0: no field is focused, so there is no keyboard to have measured. Leaving the
      // previous reading standing would make the readout report a stale number as current.
      pb._pbKbMeasured = 0;
      return;
    }
    const sample = readViewportSample();
    const measured = measureKeyboardInset(sample);
    // Hoisted above the memory below: how much room iOS made for itself this frame.
    //
    // MKB-4c: the pan is not the whole of it. iOS makes this room two ways — panning the
    // visual viewport, and shoving the fixed layer — and the CEO's 2026-08-30 screenshots
    // show the shove is reported FIRST, with the pan still reading 0 for a frame or more.
    // Reading the pan alone in that window says "no room made", so the sheet pays for the
    // keyboard on top of a layer iOS has already lifted by the keyboard: the sheet lands at
    // -347 to 81, the text field above the top of the screen, and snaps back when the pan
    // finally arrives. That is the bounce the CEO photographed.
    //
    // `corroboratedDisplacement` is the one rule that decides it, and it refuses to guess:
    // where neither the pan nor the document scroll agrees with the layer's displacement it
    // answers null, and the pan alone is used, exactly as before this slice.
    const rawPan = window.visualViewport ? Math.max(0, Math.round(window.visualViewport.offsetTop)) : 0;
    // A corroborated displacement is a FLOOR for as long as the field holds focus, not a
    // per-frame reading. The two witnesses are not obliged to move in lockstep — MTFK's State
    // D recorded `vv.offsetTop 271` beside `scrollY 311` on one frame — so a single frame
    // where neither agrees with the layer is an absence of evidence, never evidence that the
    // layer sprang back. Without the floor such a frame drops the displacement to the raw pan
    // and the sheet is shortened by the whole keyboard again on a layer that never moved:
    // 347 -> 1 -> 347 -> 0, a shimmer where this slice removed a jump. The listener above is
    // what makes that reachable, because it puts a measurement on frames that used to have
    // none, so the floor and the listener arrive together on purpose.
    //
    // It is a memory of a measurement, like `_pbKbKnownInset`, not a correction of anything.
    // It self-corrects the moment the witnesses agree again — including agreeing on zero,
    // which is what a layer settling back reports — and `clearKeyboardExpectation` drops it
    // with the focus that earned it.
    const pan = Math.max(rawPan, displacementRoom(sample ? sample.crop : 0, rawPan));
    // MKB-0: publish the RAW measurement, before the expectation floor is applied below.
    // Diagnostic only — nothing reads it but the readout. It has to live here because
    // `measured` is otherwise a local that never escapes: `pb._pbKbInset` is the value AFTER
    // `Math.max(measured, expected)`, so the two are equal by construction and the one state
    // worth photographing — a measurement of 0 overridden by a guess — cannot be seen from
    // outside this function. Remove with the rest of the readout at programme close.
    pb._pbKbMeasured = measured;
    // Remember a real keyboard for the next pre-position on this screen width. A rotation
    // changes the width, which silently invalidates the memory.
    // MKB-3: remember the WHOLE keyboard, not the share the sheet happened to pay for it.
    //
    // Under a pan the sheet pays only the remainder, so storing `measured` remembers a
    // keyboard shorter than the real one — and the next focus pre-rises by that short value.
    // Device reading 5 caught it from the other side: `kn 264@430` beside `exp 325`, a
    // memory and a guess that could not both be describing the same keyboard. The total is
    // the honest quantity, and it makes the next pre-rise land exactly.
    const totalMeasured = measured + pan;
    if (totalMeasured >= KEYBOARD_MIN_INSET) {
      pb._pbKbKnownInset = { width: window.innerWidth, inset: totalMeasured };
      // A real keyboard exists — the no-keyboard release is off the table.
      if (pb._pbKbExpectTimer) {
        clearTimeout(pb._pbKbExpectTimer);
        pb._pbKbExpectTimer = null;
      }
    }
    // MTFK-G: the expectation is a FLOOR while the field is focused, never a substitute
    // for measurement. It is set only at a genuine tap (see the focusin handler), it never
    // lets the layout drop back into the keyboard's space mid-rise, and the measured value
    // takes over the moment it exceeds it. See expectedKeyboardInset for why: with the
    // field already above the keys at focus time, iOS has nothing to reveal and never pans.
    // A floor armed for a different screen width is stale — a rotation happened while the
    // field held focus — and squeezing a landscape sheet by a portrait keyboard would
    // collapse it, so a stale floor is dropped rather than applied.
    if (pb._pbKbExpected && pb._pbKbExpectedWidth !== window.innerWidth) {
      clearKeyboardExpectation();
    }
    // MKB-3, rule 2: never subtract the keyboard twice.
    //
    // The room the shopper actually gets is made by two parties. iOS makes some of it by
    // panning its visual viewport (`offsetTop`), and the sheet makes the rest by shortening
    // itself (`--pb-kb-inset`). The old code floored the SHEET'S SHARE against a remembered
    // guess and ignored the browser's, so whatever iOS had already done was taken a second
    // time — device reading 6: iOS panned by the keyboard's full 346, the measurement
    // correctly answered 0 because no overlap was left, and the floor removed another 346
    // anyway, putting the whole sheet above the visible band with only Continue showing.
    //
    // So the floor applies to the TOTAL, and the sheet contributes only the remainder. One
    // idea rather than two, and it is right in every state without a special case:
    //
    //   iOS has done nothing yet   pan 0   → total floor is the guess, sheet pays it all.
    //                                        The pre-rise is unchanged.
    //   iOS has done part of it    pan 41  → sheet pays the difference, not the whole.
    //   iOS has done all of it     pan 346 → sheet pays nothing. Reading 6, fixed.
    //
    // The first cut of this suppressed the guess outright whenever `offsetTop > 0`, and a
    // reviewer caught what that costs: iOS's pan can appear PART WAY through the rise, and
    // the inset then collapses from the pre-rise value to 0 and climbs back in steps
    // (325 → 0 → 133 → 198 → 264), applied instantly because the property is deliberately
    // untransitioned. That is the mid-rise bounce rounds 1-19 were fought over, re-created.
    // Flooring the total descends smoothly instead: 325 → 315 → 305 → 294 → 284.
    //
    // The remaining overshoot — 284 where the keyboard needs 264 — is the guess being a
    // stale memory of a taller keyboard, and it releases itself one bounded step downward
    // when KB_EXPECT_GRACE_MS expires. That release already existed and is unchanged.
    const totalFloor = pb._pbKbExpected || 0;
    const totalRoom = Math.max(measured + pan, totalFloor);

    // MKB-2, rule 1: only a real, visible field may move the layout.
    //
    // A tap is in flight and the caret is parked on the invisible shim. Moving the sheet now
    // moves the field out from under the finger before its click can land, and the click is
    // the only thing that was ever going to complete the hand-off. That is not a theory: the
    // device trail shows the viewport event arriving 83ms after the shim took focus, and the
    // hand-off never completing afterwards.
    //
    // The measurement above still ran and is still remembered — the readout stays truthful,
    // and the keyboard we are learning about is the same one either way. Only the WRITE
    // waits, for at most HANDOFF_TIMEOUT_MS, after which the watchdog ends the flight and
    // calls back here.
    if (pb._pbKbHandoff) return;
    writeKeyboardInset(Math.max(0, totalRoom - pan));
  }

  /**
   * Coalesce to one measure-and-write per animation frame.
   *
   * Reading the shell's rect forces a synchronous layout, and writing the inset invalidates
   * one. iOS fires viewport events throughout the keyboard's slide, so doing both on every
   * event is a forced layout per event on the slowest device in the range. This is a batch,
   * not an ease: the value written is still whatever the viewport says at that moment.
   *
   * This used to be a 48-frame settle-watch (MTFK-E), re-measuring until the shell stopped
   * moving — needed because iOS displaced the shell over several frames and fired no event
   * when it finished. The document scroll lock makes that displacement impossible, so the
   * watch went with it: one frame, one reading, done.
   */
  function scheduleKeyboardSync() {
    if (document.visibilityState === 'hidden') return;
    if (pb._pbKbSyncRaf) return;
    pb._pbKbSyncRaf = requestAnimationFrame(() => {
      pb._pbKbSyncRaf = 0;
      if (!document.body.classList.contains('builder-open')) return;
      syncKeyboardInset();
    });
  }

  /* MTFK-8: there is deliberately no scroll corrector here any more.
   *
   * iOS scrolls the page to reveal a focused input when the keyboard opens from closed.
   * An earlier version answered every viewport event with `window.scrollTo(0, 0)`, which
   * is a tug-of-war: iOS scrolls to reveal, we scroll back, iOS tries again, and each
   * round trips another viewport event and another forced layout. That is what "struggles
   * to find its place, about ten seconds to settle" looks like, and it matched the report
   * exactly — it happens opening from a closed keyboard, which is the only time iOS does
   * that reveal-scroll, and not moving between fields, when the keyboard never leaves.
   *
   * The scroll is removed instead of fought: `html` and `body` are both locked while the
   * builder is open (see pb-css), so the layout viewport cannot move and the fixed sheet
   * cannot be dragged with it. A `scrollTo` could not have helped with the other half in
   * any case — iOS also shifts the VISUAL viewport, which no scroll API reaches, and the
   * measurement already accounts for that through `vv.offsetTop`.
   */

  /**
   * Start the preview at the top of its own scroller. `.pb-modal-body` scrolls, and the
   * sheet shrinking around it can leave it parked with the question off the top. Done on
   * focus, before the keyboard rises, so it costs no visible movement.
   */
  function resetPreviewScroll() {
    const body = document.querySelector('#product-builder-root .pb-modal-body');
    if (body && body.scrollTop !== 0) body.scrollTop = 0;
  }

  function clearKeyboardInset() {
    if (pb._pbKbSyncRaf) {
      cancelAnimationFrame(pb._pbKbSyncRaf);
      pb._pbKbSyncRaf = 0;
    }
    if (pb._pbKbBlurTimer) {
      clearTimeout(pb._pbKbBlurTimer);
      pb._pbKbBlurTimer = null;
    }
    pb._pbKbFocused = false;
    clearKeyboardExpectation();
    // MKB-2: a flight that outlives the builder would fire its watchdog into a closed sheet.
    clearShimHandoff();
    pb._pbKbInset = 0;
    clearChromeCrop(); // MKB-4: written and cleared by the same pair as the inset.
    pb._pbKbMeasured = 0; // MKB-0 diagnostic — cleared with everything else it shadows.
    document.body.classList.remove('pb-keyboard-open');
    const root = document.getElementById('product-builder-root');
    if (root) {
      root.style.removeProperty('--pb-kb-inset');
    }
  }

  /** Back-compat name — boot and the open path call this to refresh the inset. */
  function _applyViewportLock() {
    syncKeyboardInset();
  }

  function updateViewportLock() {
    syncKeyboardInset();
  }

  // A suspended page can retain focus while losing its keyboard, or lose focus without
  // delivering blur. Gesture floors and handoff timers belong to the old interaction.
  // Reconcile only chrome: never reopen a flow, focus a field, or rehydrate shopper data.
  function suspendKeyboardLayout() {
    cancelPendingFocus();
    clearKeyboardInset();
    pb._pbKbKnownInset = null;
    pb._pbLastPointerTs = 0;
  }

  function resumeKeyboardLayout() {
    if (document.visibilityState === 'hidden') return;
    if (!document.body.classList.contains('builder-open')) return;
    suspendKeyboardLayout();
    const active = document.activeElement;
    pb._pbKbFocused = !!(active && active.id !== KEYBOARD_SHIM_ID && isKeyboardField(active));
    scheduleKeyboardSync();
  }

  function bindViewportLock() {
    if (!window.visualViewport) return;
    if (pb._vvLockBound) {
      if (document.body.classList.contains('builder-open')) updateViewportLock();
      return;
    }
    const handler = function () {
      if (!document.body.classList.contains('builder-open')) return;
      scheduleKeyboardSync();
    };
    pb._vvLockHandler = handler;
    pb._pbLayoutVisibilityHandler = function () {
      if (document.visibilityState === 'hidden') suspendKeyboardLayout();
      else resumeKeyboardLayout();
    };
    pb._pbLayoutPageShowHandler = function (event) {
      if (event.persisted) resumeKeyboardLayout();
    };
    document.addEventListener('visibilitychange', pb._pbLayoutVisibilityHandler);
    window.addEventListener('pagehide', suspendKeyboardLayout);
    window.addEventListener('pageshow', pb._pbLayoutPageShowHandler);
    window.visualViewport.addEventListener('resize', handler);
    window.visualViewport.addEventListener('scroll', handler);
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);
    // MKB-4c: the silent shove.
    //
    // iOS displaces the fixed layer to reveal the focused field and fires NOTHING on the
    // visual viewport for it — the same silence MTFK-E's 48-frame settle-watch existed to
    // survive. What it does move is the document: the CEO's screenshot of the bounce reads
    // `sy347` beside `vv t0`. So the document scroll is the event that says "the layer just
    // moved", and without it nothing re-measures until the pan is reported a frame or more
    // later, which is the gap the sheet spends in the wrong place.
    //
    // LISTENING IS NOT CORRECTING. MTFK-8 removed a `scrollTo` that answered every viewport
    // event and fought iOS for the scroll position; the one `scrollTo` left in this file is
    // the restore on close, and this adds none. All this does is schedule the same measure
    // the other three events already schedule, coalesced to one per animation frame.
    window.addEventListener('scroll', handler, { passive: true });
    pb._vvOrientationHandler = handler;
    pb._vvLockBound = true;
  }

  function unbindViewportLock() {
    clearKeyboardInset();
    cancelPendingFocus();
    if (!pb._vvLockBound || !window.visualViewport) return;
    const handler = pb._vvLockHandler;
    document.removeEventListener('visibilitychange', pb._pbLayoutVisibilityHandler);
    window.removeEventListener('pagehide', suspendKeyboardLayout);
    window.removeEventListener('pageshow', pb._pbLayoutPageShowHandler);
    pb._pbLayoutVisibilityHandler = null;
    pb._pbLayoutPageShowHandler = null;
    window.visualViewport.removeEventListener('resize', handler);
    window.visualViewport.removeEventListener('scroll', handler);
    window.removeEventListener('resize', handler);
    window.removeEventListener('orientationchange', handler);
    window.removeEventListener('scroll', handler);
    pb._vvOrientationHandler = null;
    pb._vvLockBound = false;
  }

  /**
   * MTFK-A: turn the mobile keyboard layout on and off. That is all it does now.
   *
   * It used to also measure the control stack on every change, write the height into a
   * custom property for CSS to pad the preview by, and keep a ResizeObserver on the stack to
   * redo that. A grid states the same layout in one declaration (see pb-css), so the
   * measurement, the property, the observer and the window listener are all gone — with them
   * went the one way the preview and the controls could disagree about their sizes.
   */
  function enableMobileKeyboardLayout() {
    if (pb._pbMobileLayoutOn) return;
    if (!isMobileViewport()) return;
    const root = document.getElementById('product-builder-root');
    if (!root) return;
    root.classList.add('pb-mobile-keyboard-layout');
    pb._pbMobileLayoutOn = true;
  }

  function disableMobileKeyboardLayout() {
    pb._pbMobileLayoutOn = false;
    const root = document.getElementById('product-builder-root');
    if (root) root.classList.remove('pb-mobile-keyboard-layout');
  }

  function _syncMobileKeyboardChrome() {
    if (!document.body.classList.contains('builder-open')) return;
    if (!isMobileViewport()) return;
    syncKeyboardInset();
  }

  /**
   * Every control in the builder that raises the keyboard — decided by what the element
   * IS, never by a list of class names. A class list is how the AI-image revise field was
   * missed; `opensKeyboard` covers any text field added later without an edit here.
   */
  /**
   * A focus counts as a genuine tap only this close to a real pointer-down. iOS raises a
   * keyboard for a focus inside a user gesture and declines one for a programmatic focus —
   * and applying the expectation for a keyboard that never comes is exactly the old
   * "sheet rises to meet nothing" bounce. So the expectation is gated on the gesture.
   */
  const POINTER_GRACE_MS = 700;

  /**
   * If no real keyboard has reported within this long of arming, the floor releases
   * itself. A hardware keyboard raises nothing on screen, and without this the sheet
   * would stay squeezed for the whole focus for keys that never came. One bounded
   * release, downward only — it cannot oscillate, and a real keyboard cancels it.
   */
  const KB_EXPECT_GRACE_MS = 1200;

  /**
   * MKB-2 — how long a tap has, AFTER the finger lifts, to complete its own hand-off.
   *
   * Measured from finger-up, not finger-down, and the distinction is the whole design. The
   * event being waited for is the synthesized `click`, and a browser cannot dispatch that
   * until `touchend` — so a deadline started at `touchstart` is really a limit on how long
   * the shopper may rest their finger. Anyone holding a tap longer than the budget would
   * have the field focused and the sheet squeezed WHILE THEIR FINGER WAS STILL DOWN, which
   * is precisely the "field moved out from under the finger" failure this slice exists to
   * end, re-created a third of a second later. A long press is an ordinary thing to do.
   *
   * Once the finger is up the click is imminent — it follows within a frame or two — so the
   * budget only has to cover that. Long enough that a healthy tap always wins the race and
   * the watchdog stays silent; short enough that a shopper whose tap died never notices it
   * was rescued.
   *
   * The keyboard is already up by then (the shim raised it), so the rescue focus is an
   * ordinary keyboard-up field-to-field move — the one iOS demonstrably honours — not an
   * attempt to raise a keyboard outside a gesture, which it would decline.
   */
  const HANDOFF_TIMEOUT_MS = 350;

  /**
   * The outer bound, from finger-down, for a gesture that never reports its end at all.
   *
   * Rule 3 says every wait has an exit, and "we never saw touchend" has to be one of them —
   * a listener lost to a re-render, a gesture the browser abandons silently. Generous,
   * because a flight only ever holds the sheet UN-squeezed (the touchstart gate requires no
   * keyboard focus, so the inset is already 0 when one starts): erring long costs a shopper
   * nothing while their finger is down, and erring short is the bug above.
   */
  const HANDOFF_GESTURE_MAX_MS = 3000;

  /**
   * A tap routed through the shim, waiting for its own click to reach the real field.
   *
   * This exists because one boolean was being asked to mean two things. `pb._pbKbFocused` is
   * true for the shim as well as for a real field (`isKeyboardField` counts it deliberately),
   * so the layout could not tell "the shopper is typing" from "the caret is parked on a 1px
   * input mid-gesture" — and squeezed for both. Reports 2, 3 and 4 are all downstream of that
   * one conflation.
   */
  function beginShimHandoff(target, touchId) {
    clearShimHandoff();
    const pending = { target: target, lifted: false, touchId: touchId };
    // Only the outer bound runs while the finger is down. The real budget starts at
    // finger-up, in endShimHandoffGesture.
    pending.timer = setTimeout(function () {
      pending.timer = null;
      completeShimHandoff();
    }, HANDOFF_GESTURE_MAX_MS);
    pb._pbKbHandoff = pending;
  }

  /**
   * Which finger, of possibly several, this event is about.
   *
   * A phone has more than one finger on it more often than a keyboard-and-mouse mental model
   * expects — a thumb resting on the sheet while the index finger taps is an ordinary grip.
   * Without this, ANY lift resolved the flight, so a second finger lifting elsewhere started
   * the budget for a tap whose own finger was still down: the same shape of fault as taking
   * the deadline from touchstart, just needing two fingers to reach.
   *
   * @returns {number|null} the touch identifier, or null when the event carries none
   */
  function touchIdOf(e) {
    const list = e && e.changedTouches;
    if (!list || !list.length) return null;
    const id = list[0] && list[0].identifier;
    return typeof id === 'number' ? id : null;
  }

  /**
   * The finger lifted, so the click is on its way. Re-base the deadline onto it.
   *
   * Idempotent: iOS fires `pointerup` and `touchend` for one lift, and a second re-base
   * would double the budget for no reason.
   */
  function endShimHandoffGesture(e) {
    const pending = pb._pbKbHandoff;
    if (!pending || pending.lifted) return;
    // Only the finger that started this flight may end it. When no identifier is available
    // — a browser that does not supply one, or a `pointerup` standing in for a touch we
    // already identified — fall through to the untracked behaviour rather than never
    // re-basing: a missed re-base would leave the shopper waiting out the outer bound.
    if (pending.touchId !== null && pending.touchId !== undefined) {
      const id = touchIdOf(e);
      if (id !== null && id !== pending.touchId) return;
      if (id === null && e && e.type === 'pointerup') return;
    }
    pending.lifted = true;
    if (pending.timer) clearTimeout(pending.timer);
    pending.timer = setTimeout(function () {
      pending.timer = null;
      completeShimHandoff();
    }, HANDOFF_TIMEOUT_MS);
  }

  function clearShimHandoff() {
    const pending = pb._pbKbHandoff;
    if (!pending) return;
    if (pending.timer) clearTimeout(pending.timer);
    pb._pbKbHandoff = null;
  }

  /**
   * The watchdog. Rule 3: every wait has an exit.
   *
   * Only rescues a caret that is genuinely stranded — the shim still holds it, and the field
   * the finger landed on is still in the document and still visible. Every other case is a
   * hand-off that worked, or a target that has gone away, and moving focus then would be the
   * bug rather than the fix.
   */
  function completeShimHandoff() {
    const pending = pb._pbKbHandoff;
    if (!pending) return;
    const target = pending.target;
    clearShimHandoff();

    const active = document.activeElement;
    const stranded = !!(active && active.id === KEYBOARD_SHIM_ID);
    const usable =
      stranded &&
      target &&
      target.isConnected !== false &&
      typeof target.getBoundingClientRect === 'function';
    if (usable) {
      const rect = target.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        try {
          target.focus({ preventScroll: true });
        } catch (err) {
          try {
            target.focus();
          } catch (err2) {}
        }
      }
    }
    // Either way the flight is over, so the layout may move again. A hand-off that completed
    // normally has already synced from its own focusin; this is the path where it did not.
    syncKeyboardInset();
  }

  function clearKeyboardExpectation() {
    pb._pbKbExpected = 0;
    // MKB-4c: the corroborated displacement is a memory of THIS focus on THIS screen. It goes
    // out with the floor that shares both — on blur, on a rotation that invalidates the
    // width, and on close — so it can never be applied to a layer that is no longer displaced.
    pb._pbKbDisplaced = 0;
    if (pb._pbKbExpectTimer) {
      clearTimeout(pb._pbKbExpectTimer);
      pb._pbKbExpectTimer = null;
    }
  }

  function armKeyboardExpectation() {
    if (!isMobileViewport() || !hasTouchKeyboard(window) || pb._pbLastPointerType === 'mouse') return;
    const known = pb._pbKbKnownInset;
    pb._pbKbExpected = expectedKeyboardInset({
      remembered: known && known.width === window.innerWidth ? known.inset : 0,
      viewportHeight: window.innerHeight,
    });
    // The floor is only meaningful on the screen it was computed for. A rotation changes
    // the width, and syncKeyboardInset drops a floor whose width no longer matches.
    pb._pbKbExpectedWidth = window.innerWidth;
    if (pb._pbKbExpectTimer) clearTimeout(pb._pbKbExpectTimer);
    pb._pbKbExpectTimer = setTimeout(() => {
      pb._pbKbExpectTimer = null;
      if (!pb._pbKbExpected) return;
      pb._pbKbExpected = 0;
      syncKeyboardInset();
    }, KB_EXPECT_GRACE_MS);
  }

  function isKeyboardField(el) {
    if (!el || !el.closest) return false;
    // The shim counts. It is a text input, it raises a real keyboard, and it is how the
    // trigger's tap holds one open across the builder's mount — excluding it meant the
    // inset ignored the very keyboard we had just asked for. Hand-off between the shim and
    // the real field is already handled by the blur timer below.
    if (el.id === KEYBOARD_SHIM_ID) return true;
    if (!el.closest('#builder-modal')) return false;
    return opensKeyboard(el);
  }

  /**
   * MTFK-G: may this touch scroll something, or would it only pan the screen?
   *
   * With the keyboard up, the layout viewport is taller than the visible one, and iOS lets
   * a drag pan the visual viewport across the difference — that is the "preview is
   * draggable" report, and no scroll lock reaches it because nothing is scrolling. The
   * only handle a page has on it is cancelling the touch. But a blanket cancel would kill
   * the builder's own scrollers (a long-text box, the design library grid), so the touch
   * is allowed wherever an ancestor inside the modal genuinely has somewhere to scroll.
   */
  function touchMayScrollSomething(el) {
    let node = el && el.nodeType === 1 ? el : null;
    while (node && node !== document.body) {
      if (opensKeyboard(node)) return true; // a text box scrolls its own text
      const style = window.getComputedStyle(node);
      const oy = style.overflowY;
      if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight + 1) return true;
      const ox = style.overflowX;
      if ((ox === 'auto' || ox === 'scroll') && node.scrollWidth > node.clientWidth + 1) return true;
      node = node.parentElement;
    }
    return false;
  }

  function bindMobileKeyboardChromeSync() {
    if (pb._pbMobileChromeSyncBound) return;
    pb._pbMobileChromePointerDown = function (e) {
      pb._pbLastPointerTs = Date.now();
      pb._pbLastPointerType = e?.pointerType || '';
    };
    pb._pbFooterMouseDown = function (e) {
      if (!isMobileViewport() || !document.body.classList.contains('builder-open')) return;
      if (e.button !== 0 || !e.cancelable) return;
      const button = e.target?.closest?.('#builder-modal .pb-modal-footer button');
      const active = document.activeElement;
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      if (!active || active.id === KEYBOARD_SHIM_ID || !isKeyboardField(active)) return;
      // WebKit blurs the field on mousedown, moving the footer before mouseup/click.
      // Keep its position through the tap; the ordinary click still owns all navigation.
      e.preventDefault();
    };
    pb._pbMobileChromeTouchStart = function (e) {
      pb._pbLastPointerType = 'touch';
      // Decided once per gesture: the walk costs a forced layout, so it never runs
      // per-move, and never at all unless a keyboard is actually up.
      pb._pbTouchMayScroll =
        !document.body.classList.contains('pb-keyboard-open') ||
        !isMobileViewport() ||
        touchMayScrollSomething(e.target);
      /* MTFK-H: raise the keyboard on the shim, never on a field inside the sheet.
       *
       * The device readout that forced this: with the field pre-positioned fully above the
       * keys (--kb-inset 347 applied at focus), iOS still displaced the fixed layer by
       * exactly the keyboard's height. So the displacement is not scroll-to-reveal at all —
       * iOS shoves the fixed layer whenever its keyboard SHOWS while focus sits inside the
       * full-screen fixed sheet, and the field's actual visibility never enters into it.
       * No layout can prevent that, and interactive-widget=resizes-content is not
       * implemented in iOS Safari, so there is no meta-tag escape either.
       *
       * What iOS demonstrably leaves alone, on this same phone: a keyboard raised by the
       * SHIM (a fixed 1px input OUTSIDE #builder-modal — the first-open path), and a focus
       * that lands on a field while the keyboard is ALREADY up (the field-to-field
       * hand-off). So a tap on a field with the keyboard closed is routed through both
       * proven paths: the shim takes focus synchronously here at touchstart and raises the
       * keyboard from outside the sheet; the tap's own click then focuses the real field a
       * beat later, which is by then an ordinary keyboard-up hand-off. The blur-grace
       * timer already treats it as one.
       *
       * MKB-2: "a beat later" was doing unguarded work. Device trail, report 2:
       * `tap input.builder-input` → `in SHIM` → `vv h428` → nothing, ever. The click that
       * was supposed to complete the hand-off never arrived, because the viewport event
       * 83ms behind it squeezed the sheet and moved the field out from under the finger.
       * The hand-off is now declared (`beginShimHandoff`) so the squeeze can wait for it,
       * and it carries a watchdog so it cannot simply not happen.
       */
      if (
        isMobileViewport() &&
        document.body.classList.contains('builder-open') &&
        !pb._pbKbFocused &&
        e.target &&
        e.target.id !== KEYBOARD_SHIM_ID &&
        isKeyboardField(e.target)
      ) {
        // The gesture gate must see this touch even if pointerdown has not fired yet.
        pb._pbLastPointerTs = Date.now();
        const shim = getKeyboardShimInput();
        if (shim) {
          try {
            shim.focus({ preventScroll: true });
          } catch (err) {
            try {
              shim.focus();
            } catch (err2) {}
          }
          // Declared only once the shim actually holds the caret — a hand-off that never
          // started needs no rescuing, and arming one would fire a watchdog at a field the
          // shopper's finger never reached. `document.activeElement` is the check, because
          // `focus()` can be declined and returns nothing either way.
          if (document.activeElement === shim) beginShimHandoff(e.target, touchIdOf(e));
        }
      }
    };
    // The finger lifted: the click is imminent, so the hand-off budget starts now. Bound to
    // both names because iOS fires pointerup and touchend for one lift and either may be the
    // one that arrives; endShimHandoffGesture is idempotent for exactly that reason.
    pb._pbMobileChromeGestureEnd = function (e) {
      endShimHandoffGesture(e);
    };
    // A cancelled gesture produces no click at all, so there is nothing left to wait for.
    // Same finger rule: another touch being cancelled says nothing about this one.
    pb._pbMobileChromeTouchCancel = function (e) {
      const pending = pb._pbKbHandoff;
      if (!pending) return;
      const id = touchIdOf(e);
      if (pending.touchId !== null && pending.touchId !== undefined && id !== null && id !== pending.touchId) {
        return;
      }
      completeShimHandoff();
    };
    pb._pbMobileChromeTouchMove = function (e) {
      if (pb._pbTouchMayScroll) return;
      if (!document.body.classList.contains('pb-keyboard-open')) return;
      if (e.cancelable) e.preventDefault();
    };
    pb._pbMobileChromeSyncFocusIn = function (e) {
      if (!isKeyboardField(e.target)) return;
      if (pb._pbKbBlurTimer) {
        // Tabbing straight from one field to the next — the keyboard never leaves.
        clearTimeout(pb._pbKbBlurTimer);
        pb._pbKbBlurTimer = null;
      }
      pb._pbKbFocused = true;
      if (!isMobileViewport()) return;
      // MTFK-H: when the shim takes focus, a tap is still in flight to a real field.
      // Squeezing the layout now would move that field out from under the finger before
      // the click lands, so the squeeze waits for the field's own focusin.
      //
      // MKB-2: it waits properly now. This early return was the whole defence, and it only
      // ever guarded the focusin path — the viewport handler squeezed anyway, because
      // syncKeyboardInset was gated on `_pbKbFocused`, which the shim sets. `_pbKbHandoff`
      // is what the layout actually consults; see syncKeyboardInset.
      if (e.target.id === KEYBOARD_SHIM_ID) return;
      // A real field has the caret: the tap completed on its own and needs no watchdog.
      clearShimHandoff();
      resetPreviewScroll();
      // MTFK-G: a genuine tap on a keyboard field means a keyboard is coming. Raise the
      // layout NOW, before it arrives, so the field is already above the keys and iOS has
      // nothing to reveal — the reveal-pan is what displaced the shell even after the
      // document was scroll-locked. Gated on a real pointer-down so a programmatic focus
      // (for which iOS declines the keyboard) never squeezes the sheet for nothing — that
      // was the original three-motion bounce, and the gate is what makes this different.
      if (Date.now() - (pb._pbLastPointerTs || 0) < POINTER_GRACE_MS) {
        armKeyboardExpectation();
      }
      // Then measure. With the keyboard ALREADY up (the trigger's shim raises it before
      // the builder mounts, and no further viewport event is coming), the measurement
      // wins; with it still closed, the expectation floor holds until it arrives.
      syncKeyboardInset();
    };
    pb._pbMobileChromeSyncFocusOut = function (e) {
      if (!isKeyboardField(e.target)) return;
      if (pb._pbKbBlurTimer) clearTimeout(pb._pbKbBlurTimer);
      // A blur that lands on another field is a hand-off, not a dismissal. One tick of
      // grace lets the matching focusin cancel this before the sheet drops.
      pb._pbKbBlurTimer = setTimeout(() => {
        pb._pbKbBlurTimer = null;
        pb._pbKbFocused = false;
        clearKeyboardExpectation();
        if (!document.body.classList.contains('builder-open')) return;
        // Straight to zero. The keyboard takes a moment to leave and covers the space the
        // sheet grows into, so there is nothing to ease.
        writeKeyboardInset(0);
      }, 0);
    };
    document.addEventListener('focusin', pb._pbMobileChromeSyncFocusIn, true);
    document.addEventListener('focusout', pb._pbMobileChromeSyncFocusOut, true);
    document.addEventListener('pointerdown', pb._pbMobileChromePointerDown, true);
    document.addEventListener('mousedown', pb._pbFooterMouseDown, true);
    document.addEventListener('touchstart', pb._pbMobileChromeTouchStart, { capture: true, passive: true });
    document.addEventListener('touchend', pb._pbMobileChromeGestureEnd, { capture: true, passive: true });
    document.addEventListener('pointerup', pb._pbMobileChromeGestureEnd, { capture: true, passive: true });
    document.addEventListener('touchcancel', pb._pbMobileChromeTouchCancel, { capture: true, passive: true });
    // passive: false is the point — a listener that cannot cancel cannot stop the pan.
    document.addEventListener('touchmove', pb._pbMobileChromeTouchMove, { capture: true, passive: false });
    pb._pbMobileChromeSyncBound = true;
  }

  function unbindMobileKeyboardChromeSync() {
    if (!pb._pbMobileChromeSyncBound) return;
    document.removeEventListener('focusin', pb._pbMobileChromeSyncFocusIn, true);
    document.removeEventListener('focusout', pb._pbMobileChromeSyncFocusOut, true);
    document.removeEventListener('pointerdown', pb._pbMobileChromePointerDown, true);
    document.removeEventListener('mousedown', pb._pbFooterMouseDown, true);
    document.removeEventListener('touchstart', pb._pbMobileChromeTouchStart, { capture: true });
    document.removeEventListener('touchend', pb._pbMobileChromeGestureEnd, { capture: true });
    document.removeEventListener('pointerup', pb._pbMobileChromeGestureEnd, { capture: true });
    document.removeEventListener('touchcancel', pb._pbMobileChromeTouchCancel, { capture: true });
    document.removeEventListener('touchmove', pb._pbMobileChromeTouchMove, { capture: true });
    // A flight outliving its own listeners would fire a watchdog nothing is watching.
    clearShimHandoff();
    pb._pbMobileChromeSyncBound = false;
  }

  /**
   * MTFK-6: build the opening field before the sheet slides, not after.
   *
   * The control pane ships from Liquid as an empty `<div data-pb-field-mount>`; the input
   * itself is built by the field registry. That build runs in `runPresentationPass`, which
   * refuses to do anything until the modal is measurable — so it cannot start until after
   * `display: block`, and in practice landed after the sheet was already on screen. The
   * question is server-rendered and the field is not, which is why the CEO saw the question
   * arrive first and the text box a beat later, with an empty space in between.
   *
   * Mounting is a re-render of the mount point, so it is safe to run again — the later pass
   * still runs and still finalises. It is skipped wherever a re-render could throw away
   * restored values before they are applied: a cart-edit session, or a resumed draft, both
   * of which arrive with selections already in state.
   */
  /**
   * Notice a keyboard field that already holds focus.
   *
   * The focusin listener is not bound until `completeOpenChrome`, so a shim focused by the
   * trigger's own tap is never seen by it — and the inset, which only moves for a focused
   * field, would ignore the keyboard that tap deliberately raised.
   */
  function adoptExistingKeyboardFocus() {
    if (pb._pbKbFocused) return;
    if (!isKeyboardField(document.activeElement)) return;
    pb._pbKbFocused = true;
    // The shim only ever holds focus because a real tap put it there, so the keyboard is
    // coming: size the sheet for it before the slide, not after the first viewport event.
    armKeyboardExpectation();
  }

  function premountFirstFieldForOpen() {
    const s = window.builderState;
    if (!s || s.mode !== 'MAIN' || s.cartEdit) return;
    const selections = (s.main && s.main.selections) || {};
    if (Object.keys(selections).length > 0) return;
    if (typeof pb.mountRegistryMainFieldMounts !== 'function') return;
    try {
      mountRegistryMainFieldMounts(undefined);
    } catch (e) {}
  }

  function open(options) {
    options = options && typeof options === 'object' ? options : {};
    if (!pb.init()) {
      alert('Loading...');
      return;
    }
    const openFocusGeneration = focusGeneration;
    const s = window.builderState;
    const childCartSub = !!(
      s && s.mode === 'SUB' && s.cartEdit && (s.cartEdit.isAddon || s.cartEdit.isBundleRow)
    );
    const cartEditSession = !!(s && s.cartEdit);
    const mobileViewport = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    const instantOpen = options.instant === true || (cartEditSession && mobileViewport);

    if (childCartSub) {
      const mainCont = document.getElementById('main-flow-container');
      const subCont = document.getElementById('sub-flow-container');
      if (mainCont) mainCont.style.display = 'none';
      if (subCont) subCont.style.display = 'flex';
    }

    if (!childCartSub) {
      if (typeof pb.reconcileStepDom === 'function') pb.reconcileStepDom();
      if (typeof pb.syncMainMaxStepsFromDom === 'function') pb.syncMainMaxStepsFromDom();
    }
    if (typeof window.pbCloseValuePropModal === 'function') window.pbCloseValuePropModal();
    clearModalClosePending();
    clearModalOpenPending();
    pb._modalClosing = false;
    pb._focusRestore =
      document.activeElement && document.activeElement.id === 'openBuilderBtn'
        ? document.activeElement
        : document.getElementById('openBuilderBtn');
    const modal = document.getElementById('builder-modal');

    function finishChildCartSubPresent() {
      if (!childCartSub) return;
      if (typeof pb.reconcileStepDom === 'function') pb.reconcileStepDom();
      if (typeof pb.updateUI === 'function') pb.updateUI();
      if (typeof pb._scheduleButtonLayoutRetry === 'function') {
        pb._scheduleButtonLayoutRetry('#sub-flow-container');
      }
    }

    let openChromeDone = false;
    function completeOpenChrome() {
      if (openChromeDone) return;
      openChromeDone = true;
      clearModalOpenPending();
      document.body.classList.remove('builder-closing');
      document.body.classList.add('builder-open');
      if (window.visualViewport && isMobileViewport()) {
        syncKeyboardInset();
      }
      if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
        enableMobileKeyboardLayout();
        bindMobileKeyboardChromeSync();
      }
      if (!pb._experienceSurface) { bindFocusTrap(); focusContainment.activate(); }
      if (typeof pb.bindButtonLayoutResizeObserver === 'function') pb.bindButtonLayoutResizeObserver();
      finishChildCartSubPresent();
      // Complete the modal even if its animation was suspended, but do not replay the
      // original gesture's autofocus after the shopper has left and returned.
      if (document.visibilityState === 'hidden' || openFocusGeneration !== focusGeneration) return;
      // MTFK-9: move focus into the dialog only if it is not already there.
      //
      // This used to focus the first focusable unconditionally, which is the close button.
      // On iOS, focusing a <button> dismisses the keyboard — so on the journey where the
      // trigger's tap held a keyboard open for a text first field, the close button threw it
      // straight back away, and nothing could reopen it without another tap from the
      // shopper. That is the "Personalize Now does not open the keyboard" report, and the
      // focus hop through the button is also why the opening felt like two events rather
      // than one. When the tap has already parked focus on the shim, leave it there and let
      // focusStepInput hand it to the real field.
      const alreadyInside =
        document.activeElement &&
        document.activeElement.closest &&
        document.activeElement.closest('#product-builder-root');
      if (!alreadyInside) {
        const focusables = getModalFocusables();
        if (focusables.length > 0) focusables[0].focus();
      }
      deferModalFocus(() => {
        const ctx = window.productBuilder.getActiveContext();
        if (ctx) {
          const st = window.builderState;
          const containerID = st.mode === 'MAIN' ? '#main-flow-container' : '#sub-flow-container';
          const prefix = st.mode === 'MAIN' ? '' : 'sub-';
          focusStepInput(containerID, prefix, ctx.step);
        }
        // The trigger's tap may have parked focus on the shim to hold the keyboard open for
        // a first field that is a text field. If focus never moved on to a real one, there
        // is nothing to type into — let the keyboard go rather than leave it hanging.
        const active = document.activeElement;
        if (active && active.id === KEYBOARD_SHIM_ID) active.blur();
      }, 50);
    }

    // MTFK-3: switch the mobile layout on and size the control stack BEFORE the sheet is
    // revealed. Doing it in completeOpenChrome meant the bottom wrapper started in normal
    // flow and flipped to bottom-anchored a beat after the preview had already painted, so
    // the background and question arrived first and the text field followed a moment later.
    // Both classes are inert until body.builder-open, so setting them early changes nothing
    // except when the browser does the work.
    if (isMobileViewport()) {
      const preRoot = document.getElementById('product-builder-root');
      if (preRoot) preRoot.classList.add('pb-mobile-keyboard-layout');
    }
    // Lock before any class lands on html/body: the lock reads window.scrollY, and the
    // overflow rules those classes switch on can clamp it to 0 first.
    lockDocumentScroll();
    modal.style.display = 'block';
    if (isMobileViewport()) {
      premountFirstFieldForOpen();
    }

    if (instantOpen) {
      modal.classList.add('open');
      completeOpenChrome();
      adoptExistingKeyboardFocus();
      if (isMobileViewport()) syncKeyboardInset();
      bindKeyboardWatch();
      bindViewportLock();
      return;
    }

    document.body.classList.remove('builder-open');
    document.body.classList.add('builder-closing');
    // If the trigger's tap already raised a keyboard for a text first field, it is up NOW.
    // Size the sheet for it before the slide, so the sheet arrives correct instead of
    // sliding up and then resizing — that second motion is what read as the background and
    // the text field being two separate animations.
    adoptExistingKeyboardFocus();
    if (isMobileViewport()) syncKeyboardInset();

    const root = document.getElementById('product-builder-root');
    let ms = 400;
    if (root) {
      const rawMs = window.getComputedStyle(root).getPropertyValue('--pb-modal-transition-ms').trim();
      const parsedMs = parseFloat(rawMs);
      if (!isNaN(parsedMs)) ms = parsedMs;
    }

    function addOpenClass() {
      modal.classList.add('open');
      const content = modal.querySelector('.pb-modal-content');
      if (content) {
        pb._modalOpenTransitionHandler = function (e) {
          if (e.target !== content || e.propertyName !== 'transform') return;
          content.removeEventListener('transitionend', pb._modalOpenTransitionHandler);
          pb._modalOpenTransitionHandler = null;
          pb._modalOpenContentEl = null;
          completeOpenChrome();
        };
        pb._modalOpenContentEl = content;
        content.addEventListener('transitionend', pb._modalOpenTransitionHandler);
      }
      pb._modalOpenFallbackTimer = setTimeout(() => {
        pb._modalOpenFallbackTimer = null;
        if (pb._modalOpenContentEl && pb._modalOpenTransitionHandler) {
          pb._modalOpenContentEl.removeEventListener('transitionend', pb._modalOpenTransitionHandler);
          pb._modalOpenTransitionHandler = null;
          pb._modalOpenContentEl = null;
        }
        completeOpenChrome();
      }, ms + 100);
    }
    if (window.requestAnimationFrame) {
      requestAnimationFrame(() => {
        requestAnimationFrame(addOpenClass);
      });
    } else {
      setTimeout(addOpenClass, 0);
    }
    bindKeyboardWatch();
    bindViewportLock();
  }

  function close(options) {
    window.pbAudioEngine?.cancelFieldOperations();
    cancelPendingFocus();
    const opts = options && typeof options === 'object' ? options : {};
    const s = window.builderState || {};
    if (
      window.PBFlowLock &&
      typeof window.PBFlowLock.hasActiveLocks === 'function' &&
      window.PBFlowLock.LOCKS &&
      window.PBFlowLock.hasActiveLocks(window.PBFlowLock.LOCKS.RECORDING)
    ) {
      if (typeof window.PBFlowLock.discardActiveRecordingsOnClose === 'function') {
        window.PBFlowLock.discardActiveRecordingsOnClose();
      }
    }
    const closeMode =
      typeof window.__pbResolveBuilderCloseMode === 'function'
        ? window.__pbResolveBuilderCloseMode(s, opts)
        : opts.force || s.mode !== 'SUB' || (s.cartEdit && s.cartEdit.isAddon)
          ? 'full'
          : 'sub-to-main';
    if (closeMode === 'sub-to-main') {
      pb.switchMode('MAIN');
      return;
    }
    if (pb._modalClosing) return;
    if (window.PBDesignLibrary && typeof window.PBDesignLibrary.flushCartPropsToSelections === 'function') {
      window.PBDesignLibrary.flushCartPropsToSelections();
    }
    pb._modalClosing = true;
    if (window.builderState.designLibrary.filterDebounce) {
      clearTimeout(window.builderState.designLibrary.filterDebounce);
      window.builderState.designLibrary.filterDebounce = null;
    }
    unbindFocusTrap();
    focusContainment.deactivate();
    const modal = document.getElementById('builder-modal');
    const content = modal.querySelector('.pb-modal-content');
    clearModalClosePending();
    clearModalOpenPending();
    modal.classList.remove('open');
    document.body.classList.remove('builder-open');
    document.body.classList.add('builder-closing');

    let finished = false;
    function finishClose() {
      if (finished) return;
      finished = true;
      pb._modalClosing = false;
      clearModalClosePending();
      modal.style.display = 'none';
      document.body.classList.remove('builder-closing');
      unlockDocumentScroll();
      if (typeof pb.unbindButtonLayoutResizeObserver === 'function') pb.unbindButtonLayoutResizeObserver();
      if (pb._focusRestore && typeof pb._focusRestore.focus === 'function') {
        // preventScroll: the line above put the page back exactly where the shopper was;
        // letting this focus scroll the trigger into view would undo that restore.
        try {
          pb._focusRestore.focus({ preventScroll: true });
        } catch (e) {
          pb._focusRestore.focus();
        }
      }
      document.dispatchEvent(new CustomEvent('pb:builder-modal-closed'));
    }

    const root = document.getElementById('product-builder-root');
    let ms = 400;
    if (root) {
      const raw = window.getComputedStyle(root).getPropertyValue('--pb-modal-transition-ms').trim();
      const parsed = parseFloat(raw);
      if (!isNaN(parsed)) ms = parsed;
    }

    if (content) {
      pb._modalCloseTransitionHandler = function (e) {
        if (e.target !== content) return;
        if (e.propertyName !== 'transform') return;
        content.removeEventListener('transitionend', pb._modalCloseTransitionHandler);
        pb._modalCloseTransitionHandler = null;
        pb._modalCloseContentEl = null;
        finishClose();
      };
      pb._modalCloseContentEl = content;
      content.addEventListener('transitionend', pb._modalCloseTransitionHandler);
    }

    pb._modalCloseFallbackTimer = setTimeout(() => {
      pb._modalCloseFallbackTimer = null;
      if (pb._modalCloseContentEl && pb._modalCloseTransitionHandler) {
        pb._modalCloseContentEl.removeEventListener('transitionend', pb._modalCloseTransitionHandler);
        pb._modalCloseTransitionHandler = null;
        pb._modalCloseContentEl = null;
      }
      finishClose();
    }, ms + 100);

    unbindKeyboardWatch();
    unbindMobileKeyboardChromeSync();
    unbindViewportLock();
    disableMobileKeyboardLayout();
  }

  function mountRegistryMainFieldMounts(options) {
    if (typeof pb.mountRegistryMainFieldMounts !== 'function') return 0;
    const opts = options && typeof options === 'object' ? options : {};
    const mainProduct = window.BUILDER_DATA && window.BUILDER_DATA.mainProduct;
    return pb.mountRegistryMainFieldMounts({
      flow: opts.flow,
      productRef: { image: (mainProduct && mainProduct.image) || '' },
    });
  }

  function activateRegistryMainStep(stepEl, options) {
    if (typeof pb.activateRegistryMainStep === 'function') {
      pb.activateRegistryMainStep(stepEl, options);
    }
  }

  function stepRegistryRenderFieldPane(step, pane) {
    const field = (step && step.field) || {};
    if (!field.type) return '';
    if (typeof pb.renderRegistryMainFieldStep !== 'function') {
      if (pane !== 'control') return '';
      return '<div class="pb-field-error" role="alert"><p class="pb-field-error-msg">Field registry is not loaded.</p><button type="button" class="builder-btn meta-btn pb-field-error-retry" onclick="window.productBuilder.runPresentationPass()">Try again</button></div>';
    }
    const out = pb.renderRegistryMainFieldStep(field, { flow: 'fresh' });
    if (!out) {
      if (pane !== 'control') return '';
      return '<div class="pb-field-error" role="alert"><p class="pb-field-error-msg">This field could not be loaded.</p><button type="button" class="builder-btn meta-btn pb-field-error-retry" onclick="window.productBuilder.runPresentationPass()">Try again</button></div>';
    }
    return pane === 'control' ? out.controlHtml || '' : out.imageHtml || '';
  }

  function createStepRegistry() {
    return {
      variants: {
        expand(builder, stepDef, steps) {
          const options = window.BUILDER_DATA?.optionsWithValues || [];
          const realOptions = (options || []).filter((option) => {
            const name = String(option?.name || '').toLowerCase();
            return option && name && name !== 'title' && (option.values || []).length > 0;
          });
          if (realOptions.length === 0) return;
          realOptions.forEach((option) => {
            steps.push(builder.createStep('variants', stepDef, { option }));
          });
        },
        renderImage(builder, step) {
          const option = step.option || {};
          if (typeof builder.renderRegistryVariantStep === 'function') {
            const out = builder.renderRegistryVariantStep(option, { mode: 'MAIN' });
            if (out && out.imageHtml) return out.imageHtml;
          }
          return '';
        },
        renderControl(builder, step) {
          const option = step.option || {};
          if (typeof builder.renderRegistryVariantStep === 'function') {
            const out = builder.renderRegistryVariantStep(option, { mode: 'MAIN' });
            if (out && out.controlHtml) return out.controlHtml;
          }
          return '';
        },
      },
      personalization: {
        expand(builder, stepDef, steps) {
          const fields = window.BUILDER_DATA?.mainProduct?.personalization || [];
          fields.forEach((field) => {
            steps.push(builder.createStep('personalization', stepDef, { field }));
          });
        },
        renderImage(builder, step) {
          return (builder || window.productBuilder)._stepRegistryRenderFieldPane(step, 'image');
        },
        renderControl(builder, step) {
          return (builder || window.productBuilder)._stepRegistryRenderFieldPane(step, 'control');
        },
      },
      offers: {
        renderImage(builder) {
          const title = window.BUILDER_DATA?.offerTitle || 'Choose your offer';
          return `<div style="padding: 20px;"><h3 style="margin: 0 0 15px 0; font-size: 18px; font-weight: bold; color: #333;">${builder.escapeAttr(title)}</h3><div id="offers-list-js"></div></div>`;
        },
        renderControl() {
          return `<div class="step-content-inner"><span style="font-size:13px; color:#666;">Select an option above to continue.</span></div>`;
        },
      },
      addons: {
        renderImage() {
          return `<div style="padding: 20px;"><h3 style="margin: 0 0 15px 0; font-size: 18px; font-weight: bold; color: #333;">Make It Even More Special</h3><div id="addons-list-container"></div></div>`;
        },
        renderControl() {
          return `<div class="step-content-inner"><span style="font-size:13px; color:#666;">Review your add-ons above before finishing.</span></div>`;
        },
      },
    };
  }

  function runPresentationPass(options) {
    options = options && typeof options === 'object' ? options : {};
    if (!isBuilderModalMeasurable()) return;
    if (pb._pbPresentationPassRunning) return;
    pb._pbPresentationPassRunning = true;
    try {
      const s = window.builderState;
      const isMain = !s || s.mode === 'MAIN';
      const isAddonCartSub = !!(s && s.cartEdit && s.cartEdit.isAddon && s.mode === 'SUB');
      const containerSel = isMain ? '#main-flow-container' : '#sub-flow-container';

      if (options.draftHydration) {
        runDraftContinuePresentationPass(pb, options.draftHydration, {
          mountRegistryMainFieldMounts,
        });
      }

      if (isMain && !options.draftHydration) {
        mountRegistryMainFieldMounts(undefined);
        if (typeof pb.finalizeFieldPresentation === 'function') {
          pb.finalizeFieldPresentation(containerSel, {
            flow: 'fresh',
          });
        } else {
          const activeMainContent = document.querySelector(
            '#main-flow-container .pb-bottom-wrapper .pb-step.active'
          );
          if (activeMainContent) {
            activateRegistryMainStep(activeMainContent, undefined);
          }
        }
      }

      pb.updateUI();
      pb._scheduleButtonLayoutRetry(containerSel);
      maybeResumeDesignLibrarySession();
      if (window.PBAiImage && typeof window.PBAiImage.init === 'function') {
        window.PBAiImage.init('#main-flow-container');
        window.PBAiImage.init('#sub-flow-container');
      }
      if (typeof pb.rehydrateActiveCustomField === 'function') {
        pb.rehydrateActiveCustomField();
      }
      if (!isAddonCartSub) {
        pb.queueStepNavigation('any', pb._pbLastMoveDirection || 1);
      }
      const lifecycle = pb._ensureLifecycle();
      if (lifecycle) {
        lifecycle.advanceTo('presented', { draftHydration: options.draftHydration });
        if (isMain) pb._emitStepActive();
      }
      setTimeout(() => {
        const activeImg = document.querySelector('#main-flow-container .pb-modal-body .pb-step.active');
        if (activeImg && typeof window.pbAutoResizeIframes === 'function') window.pbAutoResizeIframes(activeImg);
      }, 200);
    } finally {
      pb._pbPresentationPassRunning = false;
    }
  }

  function runChildCartEditPresentationPass() {
    if (!isBuilderModalMeasurable()) return;
    const s = window.builderState;
    const manifest =
      window.PBEntryManifest && typeof window.PBEntryManifest.readEntryManifest === 'function'
        ? window.PBEntryManifest.readEntryManifest(s)
        : null;
    if (
      manifest &&
      window.PBEntryManifest &&
      typeof window.PBEntryManifest.isMainCartEditManifest === 'function' &&
      window.PBEntryManifest.isMainCartEditManifest(manifest)
    ) {
      return;
    }
    const isChildCartSub = !!(
      s && s.mode === 'SUB' && s.cartEdit && (s.cartEdit.isAddon || s.cartEdit.isBundleRow)
    );
    if (!isChildCartSub) return;

    const mainCont = document.getElementById('main-flow-container');
    const subCont = document.getElementById('sub-flow-container');
    if (mainCont) mainCont.style.display = 'none';
    if (subCont) subCont.style.display = 'flex';

    if (typeof pb.reconcileStepDom === 'function') pb.reconcileStepDom();
    pb.updateUI();
    pb._scheduleButtonLayoutRetry('#sub-flow-container');
    maybeResumeDesignLibrarySession();
    if (window.PBAiImage && typeof window.PBAiImage.init === 'function') {
      window.PBAiImage.init('#sub-flow-container');
    }
    if (typeof pb.rehydrateActiveCustomField === 'function') {
      pb.rehydrateActiveCustomField();
    }
    const lifecycle = pb._ensureLifecycle();
    if (lifecycle) {
      lifecycle.advanceTo('presented');
      pb._emitStepActive();
    }
  }

  function runMainCartEditPresentationPass() {
    if (!isBuilderModalMeasurable()) return;
    if (
      window.PBCartEditPresenter &&
      typeof window.PBCartEditPresenter.runMainCartEditPresentationPass === 'function'
    ) {
      window.PBCartEditPresenter.runMainCartEditPresentationPass(pb);
      return;
    }
    const s = window.builderState;
    const isMainCartEdit = !!(s && s.mode === 'MAIN' && s.cartEdit && !s.cartEdit.isAddon && !s.cartEdit.isBundleRow);
    if (!isMainCartEdit) return;
    runPresentationPass();
  }

  function resumeBuilderChrome() {
    if (typeof pb.syncBuilderLayoutForActiveStep === 'function') {
      pb.syncBuilderLayoutForActiveStep();
    }
    if (typeof pb.rehydrateActiveCustomField === 'function') {
      pb.rehydrateActiveCustomField();
    }
    if (window.PBDesignLibrary && typeof window.PBDesignLibrary.scheduleResumeBuilderSession === 'function') {
      window.PBDesignLibrary.scheduleResumeBuilderSession();
    } else if (window.PBDesignLibrary && typeof window.PBDesignLibrary.activateCurrentStepWraps === 'function') {
      window.PBDesignLibrary.activateCurrentStepWraps();
      if (typeof pb.checkStepValidity === 'function') {
        pb.checkStepValidity();
      }
    } else if (typeof pb.checkStepValidity === 'function') {
      pb.checkStepValidity();
    }
  }

  function goToMainPersonalizationStep() {
    const fields = pb.getPersonalizationFields();
    mountRegistryMainFieldMounts({ flow: 'draft_resume' });
    if (pb.hydrateFieldContainer) {
      pb.hydrateFieldContainer('#main-flow-container', {
        flow: 'draft_resume',
        fields: fields,
        stateSlice: window.builderState.main,
        fromDraft: true,
      });
    }
    const n = (window.BUILDER_DATA && window.BUILDER_DATA.firstPersonalizationStep) || 1;
    pb.jumpToMainStep(n);
  }

  function initCustomFields(sel) {
    if (!window.PBToolkit) return;
    const root = typeof sel === 'string' ? document.querySelector(sel) : sel;
    if (!root) return;
    root.querySelectorAll('.pb-custom-inputs').forEach((inputsEl) => {
      if (inputsEl.dataset.pbtkInit === 'true') return;
      const label = inputsEl.dataset.customLabel || inputsEl.getAttribute('data-custom-label') || '';
      const code = inputsEl.getAttribute('data-custom-code') || inputsEl.dataset.customCode || '';
      if (!code || !code.trim()) return;
      if (isDeclarativeDerivedPropertiesSettings(code)) return;
      const stepEl = inputsEl.closest('.pb-step');
      const stepId = stepEl ? stepEl.id : '';
      const imgId = stepId.replace('content-page-', 'img-page-').replace('sub-content-page-', 'sub-img-page-');
      const previewEl = document.getElementById(imgId);
      const previewContainer = previewEl ? previewEl.querySelector('.pb-custom-preview') : null;
      window.PBToolkit.init(inputsEl, previewContainer, { label: label });
      inputsEl.dataset.pbtkInit = 'true';
    });
  }

  function resolvePresentationSelector(sel) {
    if (typeof sel === 'string') return sel;
    if (sel && sel.nodeType === 1) {
      if (sel.id) return '#' + sel.id;
      return sel;
    }
    return '#main-flow-container';
  }

  function applyInputSettings(sel) {
    if (pb.isUnifiedFieldEngineEnabled && pb.isUnifiedFieldEngineEnabled()) {
      if (typeof pb.finalizeFieldPresentation === 'function') {
        pb.finalizeFieldPresentation(sel);
      }
      pb.syncPersonalizationTemplates(sel);
      initCustomFields(sel);
      if (window.PBAiImage && typeof window.PBAiImage.init === 'function') window.PBAiImage.init(sel);
      if (window.pbLongTextRecorder && typeof window.pbLongTextRecorder.init === 'function') {
        window.pbLongTextRecorder.init(sel);
      }
      if (window.pbAudioRecorder && typeof window.pbAudioRecorder.init === 'function') window.pbAudioRecorder.init(sel);
      pb.checkStepValidity();
      return;
    }
    const selector = resolvePresentationSelector(sel);
    const ctx = pb.getActiveContext();
    const scopeRoot = typeof selector === 'string' ? document.querySelector(selector) : selector;
    if (!scopeRoot) return;
    scopeRoot.querySelectorAll('.builder-input').forEach((input) => {
      const settings = pb.parseSettings(input.dataset.settings || '');
      if (settings.uppercase) input.value = input.value.toUpperCase();
      pb.validateTextInput(input, settings);
      pb.updateInputMeta(input, settings);
      const rawPlaceholder = settings.placeholder ? settings.placeholder.trim().replace(/"/g, '') : '';
      const resolvedPlaceholder = rawPlaceholder ? pb.resolvePersonalizationTokens(rawPlaceholder, ctx) : '';
      input.placeholder = resolvedPlaceholder || 'Type here...';
      if (
        input.tagName === 'TEXTAREA' &&
        (input.classList.contains('pb-long-text') || input.classList.contains('pb-topic-answer'))
      ) {
        pb.resizeLongText(input);
      }
    });
    scopeRoot.querySelectorAll('.btn-grid').forEach((grid) => {
      if (grid.classList.contains('pb-topic-multi') && grid.dataset.topicList) {
        const parsed = pb.parseTopicSettings(grid.dataset.topicList || '');
        const currentRows = grid.querySelectorAll('.pb-topic-row').length;
        const willRender = currentRows !== parsed.topics.length || grid.dataset.topicRendered !== 'true';
        if (willRender) {
          pb.renderTopicButtons(grid, parsed.topics);
        }
        pb.applyTopicRules(grid, parsed.rules);
      } else {
        const otherBtn = grid.querySelector('.meta-btn[data-other="true"]');
        if (otherBtn) {
          const label = otherBtn.dataset.label;
          const currentVal = String(ctx.selections[label] || '');
          const selectedValues = currentVal.split(',').map((v) => v.trim()).filter(Boolean);
          const values = Array.from(grid.querySelectorAll('.meta-btn')).map((b) => b.dataset.value);
          const knownSelected = selectedValues.filter((v) => values.indexOf(v) !== -1);
          const unknownSelected = selectedValues.filter((v) => values.indexOf(v) === -1);
          grid.querySelectorAll('.meta-btn').forEach((b) => {
            if (b.dataset.other === 'true') return;
            b.classList.toggle('active', knownSelected.indexOf(b.dataset.value) !== -1);
          });
          const isOtherWithCustom = unknownSelected.length > 0;
          if (isOtherWithCustom) {
            otherBtn.classList.add('active');
            const step = grid.closest('.pb-step');
            let wrap = null;
            if (step) {
              step.querySelectorAll('.pb-other-input-wrap').forEach((w) => {
                if (w.dataset.label === label) wrap = w;
              });
            }
            if (wrap) {
              wrap.style.display = '';
              const input = wrap.querySelector('input.pb-other-input');
              if (input) input.value = unknownSelected.join(', ');
            }
          } else {
            otherBtn.classList.remove('active');
          }
        }
        pb.validateButtonGrid(grid);
      }
    });
    scopeRoot.querySelectorAll('.pb-audio-wrap').forEach((wrap) => {
      const hidden = wrap.querySelector('input.builder-required[data-label]');
      if (!hidden) return;
      const settingsStr = (wrap.dataset.settings || '').trim();
      if (settingsStr) {
        const settings = pb.parseSettings(settingsStr);
        hidden.dataset.required = settings && settings.required ? 'true' : 'false';
      }
    });
    scopeRoot.querySelectorAll('.pb-upload-wrap').forEach((wrap) => {
      if (wrap.closest('.pb-ai-image-wrap')) return;
      const hidden = wrap.querySelector('input.builder-required[data-label]');
      if (!hidden) return;
      const settingsStr = (wrap.dataset.settings || '').trim();
      if (settingsStr) {
        const settings = pb.parseSettings(settingsStr);
        hidden.dataset.required = settings && settings.required ? 'true' : 'false';
      }
      const urls = pb.getUploadUrls ? pb.getUploadUrls(wrap) : [];
      if (pb.renderUploadPreviews) pb.renderUploadPreviews(wrap, urls || []);
    });
    pb.syncPersonalizationTemplates(sel);
    initCustomFields(sel);
    if (window.PBAiImage && typeof window.PBAiImage.init === 'function') window.PBAiImage.init(sel);
    if (window.pbLongTextRecorder && typeof window.pbLongTextRecorder.init === 'function') {
      window.pbLongTextRecorder.init(sel);
    }
    if (window.pbAudioRecorder && typeof window.pbAudioRecorder.init === 'function') window.pbAudioRecorder.init(sel);
    pb.checkStepValidity();
  }

  return {
    isBuilderModalMeasurable,
    clearModalClosePending,
    clearModalOpenPending,
    getModalFocusables,
    bindFocusTrap,
    unbindFocusTrap,
    focusContainment,
    open,
    close,
    focusStepInput,
    ensureInputVisible,
    getKeyboardShimInput,
    bindKeyboardWatch,
    unbindKeyboardWatch,
    _applyViewportLock,
    syncKeyboardInset,
    updateViewportLock,
    bindViewportLock,
    unbindViewportLock,
    enableMobileKeyboardLayout,
    disableMobileKeyboardLayout,
    _syncMobileKeyboardChrome,
    bindMobileKeyboardChromeSync,
    unbindMobileKeyboardChromeSync,
    _mountRegistryMainFieldMounts: mountRegistryMainFieldMounts,
    _activateRegistryMainStep: activateRegistryMainStep,
    _stepRegistryRenderFieldPane: stepRegistryRenderFieldPane,
    createStepRegistry,
    runPresentationPass,
    runChildCartEditPresentationPass,
    runMainCartEditPresentationPass,
    resumeBuilderChrome,
    goToMainPersonalizationStep,
    applyInputSettings,
    initCustomFields,
  };
}

const MODAL_METHODS = [
  'isBuilderModalMeasurable',
  'clearModalClosePending',
  'clearModalOpenPending',
  'getModalFocusables',
  'bindFocusTrap',
  'unbindFocusTrap',
  'open',
  'close',
  'focusStepInput',
  'ensureInputVisible',
  'getKeyboardShimInput',
  'bindKeyboardWatch',
  'unbindKeyboardWatch',
  '_applyViewportLock',
  'syncKeyboardInset',
  'updateViewportLock',
  'bindViewportLock',
  'unbindViewportLock',
  'enableMobileKeyboardLayout',
  'disableMobileKeyboardLayout',
  '_syncMobileKeyboardChrome',
  'bindMobileKeyboardChromeSync',
  'unbindMobileKeyboardChromeSync',
  '_mountRegistryMainFieldMounts',
  '_activateRegistryMainStep',
  '_stepRegistryRenderFieldPane',
  'runPresentationPass',
  'runChildCartEditPresentationPass',
  'runMainCartEditPresentationPass',
  'resumeBuilderChrome',
  'goToMainPersonalizationStep',
  'applyInputSettings',
  'initCustomFields',
];

function attachToWindow() {
  if (typeof window === 'undefined') return;
  window.PBModalChrome = { createModalChrome };
}

function install(pb) {
  if (!pb || !window.PBModalChrome) return false;
  if (pb._pbModalChromeInstalled) return true;
  const chrome = createModalChrome({ pb });
  pb._pbModalChromeInstalled = true;
  pb._pbModalChrome = chrome;
  MODAL_METHODS.forEach((name) => {
    pb[name] = function (...args) {
      return chrome[name](...args);
    };
  });
  pb.stepRegistry = chrome.createStepRegistry();
  return true;
}

function tryInstall() {
  if (typeof window === 'undefined' || !window.productBuilder) return false;
  return install(window.productBuilder);
}

attachToWindow();

if (typeof window !== 'undefined') {
  window.__pbEnsureModalChrome = install;
  if (!tryInstall()) {
    document.addEventListener('DOMContentLoaded', tryInstall);
    document.addEventListener('builder:ready', tryInstall);
  }
}
